type ElectronWindow = typeof window & {
  process: {type: string} | undefined;
  api:
    | {
        receive: (evt: string, handler: () => void) => void;
      }
    | undefined;
};

/** Check if the current browser this application is running in is electron
 * copied and modified from https://raw.githubusercontent.com/cheton/is-electron/master/index.js
 * https://github.com/electron/electron/issues/2288
 */
export const isElectron = (): boolean => {
  if (
    (typeof process !== 'undefined' &&
      typeof process.versions === 'object' &&
      Boolean(process.versions.electron)) ||
    (typeof window !== 'undefined' &&
      typeof window.process === 'object' &&
      (window as ElectronWindow).process.type === 'renderer') ||
    (typeof navigator.userAgent === 'string' &&
      navigator.userAgent.indexOf('Electron') >= 0)
  ) {
    return true;
  }
  return false;
};

type MessageBasic = {type: string};

/**
 * Sends a message to the electron backend without repeating the "window.postMessage" stuff every time.
 */
export const sendElectronMessage = (message: MessageBasic): void => {
  if (isElectron()) {
    window.postMessage(message, window.location.origin);
  }
};

//we have to allow all kinds of payloads here as long as the typesafety is guaranteed on the outside we should be safe.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const electronMessageHandlers: {[key: string]: (payload: any) => void} = {};

/**
 * Sends a message with the specified messageType to the lexian-browser and handles the response's
 * payload with the specified responsePayloadHandler.
 * If the type is not included in the response the event is _not_ handled.
 */
export const exchangeElectronMessage = <T extends MessageBasic, U>(
  message: T,
  responsePayloadHandler: (response: U) => void
): void => {
  if (isElectron()) {
    electronMessageHandlers[message.type] = responsePayloadHandler;
    sendElectronMessage(message);
  }
};

/**
 * Cancel an exception for the given message type.
 */
export const cancelExchange = (type: string): void => {
  delete electronMessageHandlers[type];
};

const mainElectronMessageHandler = (
  event: MessageEvent<{
    type: string;
    payload: {type: string; payload: unknown};
  }>
) => {
  if (!isElectron()) {
    return;
  }

  // The event's data type needs to be renderer, otherwise we could
  // unintentionally remove the eventListener by registering any random message
  // posted to the window.
  if (event.data.type !== 'renderer') {
    return;
  }

  const {type, payload} = event.data.payload;

  const handler = electronMessageHandlers[type];

  if (!handler) {
    return;
  }

  handler(payload);

  cancelExchange(type);
};

window.addEventListener('message', mainElectronMessageHandler);
